import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Lock } from "lucide-react";

import { cn } from "../lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useSecoesDoMenu } from "./MainListItems";

// Mesma regra de "estar ativo" que o NavLink da lateral aplica: a rota "/" só
// acende na própria raiz (senão o Dashboard ficaria aceso em toda tela), e as
// demais acendem também nas telas filhas (/crm/leads/12 mantém Leads aceso).
// Precisamos dela solta porque o gatilho da seção é um <button>, não um link —
// ele não tem o `isActive` do NavLink para se pintar.
function rotaAtiva(pathname, to) {
  if (to === "/") return pathname === "/";
  const alvo = to.toLowerCase();
  const atual = pathname.toLowerCase();
  return atual === alvo || atual.startsWith(`${alvo}/`);
}

// Item dentro do menu suspenso. Não reaproveita o NavItem da lateral de
// propósito: ali o item é uma linha de coluna estreita com badge posicionado
// por cima do ícone; aqui ele vive dentro de um DropdownMenuItem do Radix, que
// precisa receber o link como filho (`asChild`) para o teclado e o foco
// continuarem funcionando.
function ItemSuspenso({ to, icon: Icon, label, badge, foraDoPlano }) {
  return (
    <DropdownMenuItem asChild>
      <NavLink
        to={to}
        title={
          foraDoPlano
            ? `${label} — não está no seu plano. Fale com quem administra a conta para liberar.`
            : undefined
        }
        className={({ isActive }) =>
          cn(
            "flex cursor-pointer items-center gap-2.5",
            isActive ? "text-primary" : "text-foreground",
            foraDoPlano && "opacity-50"
          )
        }
      >
        <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
          <Icon className="h-4 w-4" />
          {badge && (
            <span className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-destructive text-[8px] font-bold text-destructive-foreground">
              !
            </span>
          )}
        </span>
        <span className="truncate">{label}</span>
        {foraDoPlano && <Lock className="ml-auto h-3 w-3 shrink-0" />}
      </NavLink>
    </DropdownMenuItem>
  );
}

// Barra de navegação horizontal — a alternativa à lateral, escolhida pelo
// usuário no menu da conta.
//
// São ~38 telas em 7 seções: enfileirar tudo não cabe em nenhuma largura, então
// cada seção vira um menu suspenso e só o item solto (Dashboard) fica visível
// como link. As seções e a filtragem por permissão/plano saem de
// `useSecoesDoMenu()`, a mesma fonte da lateral — assim uma tela nova aparece
// nos dois lugares sem ninguém lembrar de duplicar.
export default function TopNav() {
  const secoes = useSecoesDoMenu();
  const { pathname } = useLocation();

  return (
    // overflow-x-auto: em janela estreita de desktop (ou com muitas seções
    // liberadas) a fila passa da largura; rolar é melhor do que espremer ou
    // cortar seção.
    <nav className="scrollbar-discreta flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
      {secoes.map((secao) => {
        // Seção sem rótulo é item solto: vira link direto, sem menu.
        if (!secao.label) {
          return secao.itens.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex shrink-0 items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
                )
              }
            >
              <item.icon className="h-4 w-4" />
              <span>{item.label}</span>
            </NavLink>
          ));
        }

        // A seção inteira acende quando a tela aberta é uma das suas — é o que
        // substitui aqui o destaque que na lateral fica no próprio item.
        const secaoAtiva = secao.itens.some((item) =>
          rotaAtiva(pathname, item.to)
        );
        // Um "!" no gatilho avisa que há algo pedindo atenção lá dentro
        // (conexão caída, hoje). Sem isso o aviso da lateral sumiria no modo
        // topo, escondido dentro do menu fechado.
        const temAviso = secao.itens.some((item) => item.badge);

        return (
          <DropdownMenu key={secao.id}>
            <DropdownMenuTrigger
              className={cn(
                "flex shrink-0 items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent",
                secaoAtiva ? "bg-primary/10 text-primary" : "text-muted-foreground"
              )}
            >
              <span>{secao.label}</span>
              {temAviso && (
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground">
                  !
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-56">
              {secao.itens.map((item) => (
                <ItemSuspenso key={item.to} {...item} />
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </nav>
  );
}
